import { useEffect, useState } from 'react'

import { useCookie, isLocalhost } from '@dhl/utils'
import { fetch } from '@dhl/fetch'

// This is not meant as configuration per app, but rather a sanity check that we are redirecting to a domain that we know about.
// This is so that when a cookie has a faulty value, the app will stay on the current domain. Instead of breaking completely.
// Nice? No. Needed? Perhaps...
const allowedTargetHosts = [
  'accept.dhlecommerce.nl',
  'accept.dhlecommerce.be',
  'econnect-accept.dhlecommerce.eu',
  'accept.dhlecommerce.se',
  'my.dhlecommerce.nl',
  'my.dhlecommerce.be',
  'econnect.dhlecommerce.eu',
  'my.dhlecommerce.se',
]

type CheckMigrationResponse = {
  startMigration: boolean
}

export type MigrationMode = 'live' | 'secret' | 'off' | 'rollback'

const sha256 = async (rawData: string) => {
  const msgBuffer = new TextEncoder().encode(rawData)
  const hashBuffer = await crypto?.subtle?.digest('SHA-256', msgBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

const accessKeyHash = '6e66ee209cd4ce85206f32b1d7102d1bdf08709afd22de9ac531755acb6cdcfa'

export const getReceiverAppMajorVersion = (userAgent: string): number | null => {
  const pattern = /DHL_WEBVIEW\(.*?\/(\d+\.\d+\.\d+)/
  const version = userAgent.match(pattern)?.[1]
  if (version && userAgent.includes('com.dhlparcel.nl')) {
    const [major] = version.split('.')
    try {
      return parseInt(major, 10)
    } catch {
      return null
    }
  } else {
    return null
  }
}

// When using `useMigrationGuardWithMode` then `mode` should come from configuration.
// Ensure you are able to change it on production, so we can rollback fast.
export const useMigrationGuardWithMode = (mode: MigrationMode) => {
  const [migratedToHost, setMigratedToHost] = useCookie('X-MIGRATED-TO-HOST', '', { raw: true })
  const [migrationAccessKey] = useCookie('X-MIGRATION-ACCESS-KEY', '', { raw: true })
  const [continueRenderApp, setContinueRenderApp] = useState(mode !== 'live' && mode !== 'secret')

  const checkServer = () =>
    fetch<unknown, CheckMigrationResponse>({
      method: 'GET',
      url: '/api/user/migrate-authentication/check',
    }).catch(
      () =>
        ({
          startMigration: false,
        }) as CheckMigrationResponse
    )

  useEffect(() => {
    const performMigration = async (predicate: boolean) => {
      if (
        predicate &&
        migratedToHost &&
        allowedTargetHosts.includes(migratedToHost) &&
        !isLocalhost() &&
        window.location.host !== migratedToHost
      ) {
        window.location.host = migratedToHost
      } else if (predicate && !isLocalhost() && !migratedToHost && (await checkServer())?.startMigration) {
        const returnToPath = encodeURIComponent(window.location.pathname + window.location.search)
        window.location.href = `/api/user/migrate-authentication/init?returnToPath=${returnToPath}`
      } else {
        setContinueRenderApp(true)
      }
    }

    const receiverAppMajorVersion = getReceiverAppMajorVersion(navigator?.userAgent ?? '')
    // Starting version 5.0.0 My DHL supports my.dhlecommerce.nl
    const appMustBeUpdatedFirst = receiverAppMajorVersion != null && receiverAppMajorVersion < 5

    if (mode === 'live') {
      performMigration(!appMustBeUpdatedFirst)
    } else if (mode === 'secret') {
      sha256(migrationAccessKey).then((hash) => {
        const onEcommerceWithSecretCookie = hash === accessKeyHash && allowedTargetHosts.includes(window.location.host)
        return performMigration(onEcommerceWithSecretCookie && !appMustBeUpdatedFirst)
      })
    } else if (mode === 'rollback') {
      setMigratedToHost('', {}) // Reset in case of rollback
      setTimeout(() => {
        if (window.location.host === 'my.dhlecommerce.nl') {
          window.location.host = 'my.dhlparcel.nl'
        } else if (window.location.host === 'accept.dhlecommerce.nl') {
          window.location.host = 'accept.dhlparcel.nl'
        }
      }, 500) // Wait a while to ensure the cookie is cleared
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [setContinueRenderApp])

  return { continueRenderApp }
}

// TODO @B2X & @SPA: Use `useMigrationGuardWithMode()` before going live.
export const useMigrationGuard = (isActive: boolean) => useMigrationGuardWithMode(isActive ? 'secret' : 'off')
